import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import useSubscriptionData from "./hooks/useSubscriptionData";
import InvoiceList from "./components/InvoiceList";
import CurrentPlanActions from "./components/CurrentPlanActions";
import PageHeader from "components/layout/page-header/PageHeader";
import styles from "./subscriptions.module.css";

const SubscriptionPage = () => {
  const token = useSelector(state => state.auth.accessToken);
  const { loading, invoices, handleManageSubscription, subscriptionDetail, fetchData } =
    useSubscriptionData(token);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const formatCurrency = (value, currency = "BRL") =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency }).format(value);

  const formatDate = dateString => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const filteredInvoices = useMemo(() => {
    if (!search) return invoices;
    const q = search.toLowerCase();
    return invoices.filter(
      inv =>
        inv.status.toLowerCase().includes(q) ||
        inv.currency.toLowerCase().includes(q)
    );
  }, [invoices, search]);

  const totalPages = Math.ceil(filteredInvoices.length / pageSize);

  const paginatedInvoices = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredInvoices.slice(start, start + pageSize);
  }, [filteredInvoices, page, pageSize]);

  const handlePageChange = num => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setPage(num);
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Sua assinatura"
          subtitle="Veja detalhes do seu plano atual e o histórico de cobranças."
          accent="Assinatura"
        />
        <div className={styles.loadingWrap}>
          <div className={styles.spinner} />
          <span>Carregando dados da assinatura...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Sua assinatura"
        subtitle="Veja detalhes do seu plano atual e o histórico de cobranças."
        accent="Assinatura"
      />

      <CurrentPlanActions
        subscriptionDetail={subscriptionDetail}
        onManage={handleManageSubscription}
      />

      <hr className={styles.divider} />

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Histórico de Faturas</h2>
        <div className={styles.searchWrap}>
          <i className={`fas fa-search ${styles.searchIcon}`} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Buscar por status ou moeda..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>

      <InvoiceList
        invoices={paginatedInvoices}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />

      {totalPages > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
            <button
              key={num}
              className={`${styles.pageBtn} ${num === page ? styles.pageBtnActive : ""}`}
              onClick={() => handlePageChange(num)}
            >
              {num}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
